import React from 'react';
import PropTypes from 'prop-types';

const glow = id => (
  <filter id={id} x="-50%" y="-50%" width="200%" height="200%">
    <feGaussianBlur in="SourceGraphic" stdDeviation="0.6" result="b" />
    <feMerge>
      <feMergeNode in="b" />
      <feMergeNode in="SourceGraphic" />
    </feMerge>
  </filter>
);

const uid = prefix => s => `${prefix}-${s}`;

/** Minimal neon-style line art to echo the “glass + glow” look. */
const QuickLinkCardArt = ({ variant, idPrefix }) => {
  const s = { width: '100%', height: '100%', display: 'block' };
  const id = uid(idPrefix);
  const gf = id('f');
  const gline = id('gline');
  const tline = id('tline');
  const aline = id('aline');
  const bline = id('bline');
  const wline = id('wline');
  const sline = id('sline');
  switch (variant) {
    case 'dashboard':
      return (
        <svg viewBox="0 0 120 100" style={s} aria-hidden>
          <defs>
            {glow(gf)}
            <linearGradient id={gline} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stopColor="#5eead4" />
              <stop offset="1" stopColor="#e879a9" />
            </linearGradient>
          </defs>
          <rect
            x="20"
            y="22"
            width="28"
            height="24"
            rx="4"
            fill="none"
            stroke={`url(#${gline})`}
            strokeWidth="1.4"
            filter={`url(#${gf})`}
            opacity="0.95"
          />
          <rect
            x="60"
            y="18"
            width="32"
            height="18"
            rx="4"
            fill="none"
            stroke={`url(#${gline})`}
            strokeWidth="1.2"
            opacity="0.85"
          />
          <rect
            x="20"
            y="54"
            width="40"
            height="26"
            rx="4"
            fill="none"
            stroke={`url(#${gline})`}
            strokeWidth="1.2"
            opacity="0.75"
          />
          <rect
            x="70"
            y="46"
            width="28"
            height="34"
            rx="4"
            fill="none"
            stroke={`url(#${gline})`}
            strokeWidth="1.2"
            opacity="0.7"
          />
        </svg>
      );
    case 'trade':
      return (
        <svg viewBox="0 0 120 100" style={s} aria-hidden>
          <defs>
            {glow(gf)}
            <linearGradient id={tline} x1="0" y1="1" x2="1" y2="0">
              <stop offset="0" stopColor="#5eead4" />
              <stop offset="1" stopColor="#f472b6" />
            </linearGradient>
          </defs>
          <path
            d="M18 72 L32 50 L50 64 L70 32 L90 40 L102 20"
            fill="none"
            stroke={`url(#${tline})`}
            strokeWidth="2.2"
            filter={`url(#${gf})`}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <line
            x1="18"
            y1="78"
            x2="104"
            y2="78"
            stroke={`url(#${tline})`}
            strokeWidth="1"
            opacity="0.5"
          />
        </svg>
      );
    case 'convert':
    case 'buy-sell':
      return (
        <svg viewBox="0 0 120 100" style={s} aria-hidden>
          <defs>
            {glow(gf)}
            <linearGradient id={aline} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stopColor="#5eead4" />
              <stop offset="1" stopColor="#a78bfa" />
            </linearGradient>
          </defs>
          <rect
            x="32"
            y="28"
            width="58"
            height="44"
            rx="6"
            fill="rgba(255,255,255,0.04)"
            stroke={`url(#${aline})`}
            strokeWidth="1.2"
            filter={`url(#${gf})`}
          />
          <path
            d="M44 50 L60 64 L80 40"
            fill="none"
            stroke={`url(#${aline})`}
            strokeWidth="2.5"
            strokeLinecap="round"
            filter={`url(#${gf})`}
          />
        </svg>
      );
    case 'assets':
      return (
        <svg viewBox="0 0 120 100" style={s} aria-hidden>
          <defs>
            {glow(gf)}
            <linearGradient id={bline} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stopColor="#f0abfc" />
              <stop offset="1" stopColor="#5eead4" />
            </linearGradient>
          </defs>
          <path
            d="M32 32 L50 20 L90 20 L90 80 L32 80 Z M50 20 L50 32 L90 32"
            fill="none"
            stroke={`url(#${bline})`}
            strokeWidth="1.4"
            filter={`url(#${gf})`}
          />
          <rect
            x="50"
            y="44"
            width="32"
            height="22"
            rx="3"
            fill="none"
            stroke={`url(#${bline})`}
            strokeWidth="1"
            opacity="0.8"
          />
        </svg>
      );
    case 'wallets':
      return (
        <svg viewBox="0 0 120 100" style={s} aria-hidden>
          <defs>
            {glow(gf)}
            <linearGradient id={wline} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0" stopColor="#5eead4" />
              <stop offset="1" stopColor="#c084fc" />
            </linearGradient>
          </defs>
          <path
            d="M18 32 C18 24 24 20 32 20 L80 20 C90 20 98 28 100 40 L100 80 H28 C20 80 16 72 16 64 V36"
            fill="none"
            stroke={`url(#${wline})`}
            strokeWidth="1.4"
            filter={`url(#${gf})`}
          />
          <circle
            cx="68"
            cy="50"
            r="4"
            fill={`url(#${wline})`}
            opacity="0.9"
          />
        </svg>
      );
    case 'streaming':
      return (
        <svg viewBox="0 0 120 100" style={s} aria-hidden>
          <defs>
            {glow(gf)}
            <linearGradient id={sline} x1="0" y1="1" x2="0" y2="0">
              <stop offset="0" stopColor="#67e8f9" />
              <stop offset="1" stopColor="#f0abfc" />
            </linearGradient>
          </defs>
          <path
            d="M28 48 L38 32 L50 50 L64 24 L80 50 L96 20"
            fill="none"
            stroke={`url(#${sline})`}
            strokeWidth="2"
            filter={`url(#${gf})`}
            strokeLinecap="round"
          />
          <rect
            x="20"
            y="62"
            width="80"
            height="18"
            rx="3"
            fill="rgba(255,255,255,0.05)"
            stroke={`url(#${sline})`}
            strokeWidth="1"
            opacity="0.7"
          />
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 120 100" style={s} aria-hidden>
          <circle
            cx="60"
            cy="50"
            r="30"
            fill="none"
            stroke="#5eead4"
            strokeWidth="1.5"
            opacity="0.5"
          />
        </svg>
      );
  }
};

QuickLinkCardArt.propTypes = {
  variant: PropTypes.string,
  idPrefix: PropTypes.string.isRequired
};

export default QuickLinkCardArt;
